/**
 * /integraciones/:slug — connect Zettapost with a specific AI client (Claude,
 * Cursor, Gemini, Codex, VS Code) via the MCP connector.
 *
 * Data-driven from `lib/integrations.ts`; layout mirrors the ChatGPT page.
 * Server injects route-specific SEO into index.html for crawlers; the effect
 * here only covers client-side SPA navigation.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Bot, Plug, Check, ArrowRight, ChevronDown, Copy, type LucideIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import {
  getIntegrationBySlug, INTEGRATIONS, CAPABILITIES, PROMPTS, MCP_URL,
} from '../lib/integrations';

function PromptCard({ icon: Icon, label, prompt }: { icon: LucideIcon; label: string; prompt: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm flex flex-col">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-brand-600">
          <Icon size={14} /> {label}
        </div>
        <button
          type="button"
          onClick={() => navigator.clipboard?.writeText(prompt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })}
          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${copied ? 'text-green-600' : 'text-slate-400 hover:text-brand-600 hover:bg-slate-100'}`}
          aria-label="Copiar prompt"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">“{prompt}”</p>
    </div>
  );
}

export function IntegrationPage() {
  const { slug } = useParams<{ slug: string }>();
  const integ = slug ? getIntegrationBySlug(slug) : undefined;

  useEffect(() => {
    if (!integ) return;
    const prev = document.title;
    document.title = `Conecta Zettapost con ${integ.name} (MCP) · Zettapost`;
    return () => { document.title = prev; };
  }, [integ]);

  if (!integ) return <Navigate to="/integraciones" replace />;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-14 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-600 bg-brand-50 border border-brand-100 rounded-full px-3 py-1">
            <Bot size={14} /> Conector MCP
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
            Gestiona tus redes <span className="text-brand-600">{integ.headlineAccent}</span>
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">{integ.subhead}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href="#conectar" className="btn-primary gap-2"><Plug size={16} /> Cómo conectarlo</a>
            <Link to="/register" className="btn-secondary">Crear cuenta gratis</Link>
          </div>
          <p className="mt-3 text-xs text-slate-400">Incluido en todos los planes activos</p>
        </div>
      </section>

      {/* What is MCP */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{integ.name} + Zettapost vía MCP</h2>
            <p className="mt-3 text-slate-600">{integ.intro}</p>
          </div>
          <ul className="space-y-2.5">
            {CAPABILITIES.map((c) => (
              <li key={c} className="flex items-start gap-2.5 text-slate-700">
                <span className="mt-0.5 h-5 w-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                  <Check size={13} />
                </span>
                {c}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How to connect */}
      <section id="conectar" className="scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Conéctalo en {integ.steps.length} pasos</h2>
            <p className="mt-2 text-slate-600">Menos de un minuto. No necesitas saber programar.</p>
          </div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 flex items-center gap-3 shadow-sm">
            <span className="text-xs font-medium text-slate-500 shrink-0">URL del servidor</span>
            <code className="flex-1 text-sm font-mono text-slate-800 truncate">{MCP_URL}</code>
          </div>

          <ol className="mt-6 space-y-4">
            {integ.steps.map((s, i) => (
              <li key={i} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <span className="shrink-0 h-8 w-8 rounded-full bg-brand-600 text-white font-semibold flex items-center justify-center">{i + 1}</span>
                <div>
                  <h3 className="font-semibold text-slate-900">{s.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Example prompts */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Qué pedirle a {integ.name}</h2>
            <p className="mt-2 text-slate-600">Ejemplos de prompts que puedes copiar y adaptar.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PROMPTS.map((p) => <PromptCard key={p.label} {...p} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">Preguntas frecuentes</h2>
          <div className="mt-8 space-y-3">
            {integ.faq.map((f, i) => (
              <details key={i} className="group rounded-xl bg-white border border-slate-200 overflow-hidden">
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition">
                  {f.q}
                  <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 shrink-0" />
                </summary>
                <div className="px-5 pb-4 text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Other integrations */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <h2 className="text-xl font-bold text-slate-900 text-center">Otras integraciones</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/chatgpt" className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700 transition">ChatGPT</Link>
            {INTEGRATIONS.filter((i) => i.slug !== integ.slug).map((other) => (
              <Link key={other.slug} to={`/integraciones/${other.slug}`} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700 transition">
                {other.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">Crea, programa y publica desde {integ.name} con Zettapost</h2>
          <p className="mt-3 text-slate-600">Crea tu cuenta, conecta tus redes y empieza a gestionarlas desde {integ.name} hoy mismo. 3 días gratis.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="btn-primary gap-2">Empezar gratis <ArrowRight size={16} /></Link>
            <Link to="/integraciones" className="btn-secondary">Ver todas las integraciones</Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
